// Eén rustig taakpaneel voor de hele app (planning-gauntlet stap 1, 25 sep 2026).
//
// Vervangt vier varianten: de TaskDetailDialog op projectdetail (modal midden over de lijst),
// de Dagstart-lade, de Vandaag-popover en de Weekplan-dialog. Vorm: een zijpaneel van rechts
// dat de lijst in beeld laat (niet-modaal: je kunt links een andere taak aanklikken), Esc
// sluit. Bovenaan het afvinkrondje met de titel; links opmerking en historie, rechts de
// eigenschappen — alles direct bewerkbaar, zonder bewerkstand of opslaan-knop.
//
// Rust (memory feedback_rustige_schermen): één accentkleur, grijstinten, geen pillen voor
// prio of type, en rood alleen voor een verlopen deadline.
//
// Schrijven: zonder `onWijzig` schrijft het paneel zelf via PATCH /Api/tasks/:id
// (planningBronnen.schrijfTaak → cache van de planningsschermen vervalt) en biedt het
// "ongedaan maken" aan (utils/ongedaan, ook Ctrl+Z). Schermen met eigen optimistische
// staat (Dagstart) geven `onWijzig(id, velden, { melding, terug })` mee en regelen de
// undo zelf met `terug`.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import {
  Autocomplete, Box, Drawer, IconButton, MenuItem, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  CheckCircle as KlaarIcon,
  Close as CloseIcon,
  RadioButtonUnchecked as OpenIcon,
} from '@mui/icons-material';
import { authFetch } from '../../utils/authHelpers';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { leesJson, meldFout } from '../../utils/apiFout';
import { schrijfTaak, haalPersonen } from '../../utils/planningBronnen';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import { effectiveTaskHours } from '../../utils/capacityCalc';
import { werkdagenInBereik } from '../../utils/dagstartLanes';
import { formatDateISO } from '../../utils/weekUtils';
import { isEigenPrive, zetPrive } from '../../utils/priveTaken';
import { onderTestbalk } from '../../utils/testbalk';
import { useAssistantZichtbaar } from '../../contexts/AssistantContext';
import FeedbackVeld from '../../pages/Dagstart/FeedbackVeld';
import TaakHistorie from './TaakHistorie';
import UitvoerderAgenda from './UitvoerderAgenda';
import {
  naarTaak, vulAan, pasToe, terugVoor, kentVelden, statusVelden, afvinkVelden, airtableUrl, isTeLaat,
  persoonOpties, contextRegel, linksUit, STATUS_OPTIES, TYPE_OPTIES, DEADLINE_SOORTEN,
} from './naarTaak';

const FONT = '"Inter", system-ui, sans-serif';
const KOP_FONT = '"Manrope", system-ui, sans-serif';
const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const ZACHT = '#9ca3af';
const LIJN = '#eceef1';
const HOVER = '#f3f4f6';
const ACCENT = '#526075';
const ROOD = '#b91c1c';

const datumLang = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
};
const urenTekst = (n) => `${String(Math.round(n * 10) / 10).replace('.', ',')}u`;

// ── stille invoervelden (op moduleniveau: binnen de parent hermounten ze per toets) ──

/**
 * Tekst die je aanklikt om te bewerken. Enter (of Cmd/Ctrl+Enter bij meerdere regels) en
 * wegklikken slaan op, Escape zet terug — en loopt níet door naar het paneel, anders klapt
 * dat dicht met je wijziging nog in het veld.
 */
function StilTekst({ waarde, onBewaar, plaatshouder, meerRegels = false, disabled = false, label, sx }) {
  const [bewerk, setBewerk] = useState(false);
  const [concept, setConcept] = useState('');
  const annuleer = useRef(false);
  const huidig = waarde === null || waarde === undefined ? '' : String(waarde);

  const start = () => {
    if (disabled) return;
    setConcept(huidig);
    annuleer.current = false;
    setBewerk(true);
  };
  const klaar = () => {
    setBewerk(false);
    if (annuleer.current) { annuleer.current = false; return; }
    const nieuw = meerRegels ? concept : concept.trim();
    if (nieuw === huidig || (!meerRegels && nieuw === huidig.trim())) return;
    onBewaar(nieuw);
  };

  if (bewerk) {
    return (
      <TextField
        autoFocus fullWidth variant="standard" multiline={meerRegels} minRows={meerRegels ? 3 : undefined}
        value={concept}
        inputProps={{ 'aria-label': label }}
        InputProps={{ disableUnderline: true }}
        onChange={(e) => setConcept(e.target.value)}
        onBlur={klaar}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); annuleer.current = true; e.currentTarget.blur?.(); setBewerk(false); }
          if (e.key === 'Enter' && (!meerRegels || e.metaKey || e.ctrlKey)) { e.preventDefault(); e.target.blur(); }
        }}
        sx={{ '& .MuiInputBase-root': { fontFamily: FONT, fontSize: 'inherit', fontWeight: 'inherit', bgcolor: HOVER, borderRadius: 1, px: 1, py: 0.5 }, ...sx }}
      />
    );
  }
  return (
    <Box
      role={disabled ? undefined : 'button'} tabIndex={disabled ? undefined : 0} aria-label={label}
      onClick={start}
      onKeyDown={(e) => { if (!disabled && e.key === 'Enter') { e.preventDefault(); start(); } }}
      sx={{
        fontFamily: FONT, color: huidig ? TEKST : ZACHT, whiteSpace: meerRegels ? 'pre-wrap' : 'normal',
        cursor: disabled ? 'default' : 'text', borderRadius: 1, px: 1, py: 0.5, mx: -1, minHeight: 28,
        wordBreak: 'break-word',
        '&:hover': disabled ? {} : { bgcolor: HOVER },
        ...sx,
      }}
    >
      {huidig || plaatshouder}
    </Box>
  );
}

/** Keuzelijst zonder rand: leest als tekst, klapt open bij klikken. */
function StilKeuze({ waarde, opties, onKies, leeg = '—', label, disabled }) {
  const lijst = waarde && !opties.includes(waarde) ? [waarde, ...opties] : opties;
  return (
    <Select
      variant="standard" disableUnderline displayEmpty fullWidth disabled={disabled}
      value={waarde || ''}
      inputProps={{ 'aria-label': label }}
      onChange={(e) => { if (e.target.value !== (waarde || '')) onKies(e.target.value); }}
      renderValue={(v) => <span style={{ color: v ? TEKST : ZACHT }}>{v || leeg}</span>}
      sx={{ fontFamily: FONT, fontSize: 13, borderRadius: 1, px: 1, mx: -1, '&:hover': { bgcolor: HOVER }, '& .MuiSelect-select': { py: 0.5 } }}
    >
      {lijst.map((o) => <MenuItem key={o} value={o} sx={{ fontFamily: FONT, fontSize: 13 }}>{o}</MenuItem>)}
    </Select>
  );
}

/** Datumveld zonder rand. Slaat op bij wegklikken of Enter (niet per ingetypt segment). */
function StilDatum({ waarde, onBewaar, label, rood = false, disabled }) {
  const [concept, setConcept] = useState(waarde || '');
  useEffect(() => { setConcept(waarde || ''); }, [waarde]);
  const bewaar = () => { if ((concept || '') !== (waarde || '')) onBewaar(concept || null); };
  return (
    <TextField
      type="date" variant="standard" fullWidth disabled={disabled}
      value={concept}
      inputProps={{ 'aria-label': label }}
      InputProps={{ disableUnderline: true }}
      onChange={(e) => setConcept(e.target.value)}
      onBlur={bewaar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); bewaar(); }
        if (e.key === 'Escape') { e.stopPropagation(); setConcept(waarde || ''); }
      }}
      sx={{
        '& .MuiInputBase-root': { fontFamily: FONT, fontSize: 13, borderRadius: 1, px: 1, mx: -1, '&:hover': { bgcolor: HOVER } },
        '& input': { py: 0.5, color: concept ? (rood ? ROOD : TEKST) : ZACHT, fontWeight: rood ? 600 : 400 },
      }}
    />
  );
}

/** Stil tekstje voor een veld waarvan de waarde (nog) onbekend is. */
const Onbekend = ({ tekst }) => (
  <Typography component="span" sx={{ fontFamily: FONT, fontSize: 13, color: ZACHT }}>{tekst}</Typography>
);

const Eigenschap = ({ label, children }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: ZACHT, mb: 0.1 }}>{label}</Typography>
    <Box sx={{ fontFamily: FONT, fontSize: 13, color: TEKST, minHeight: 28, display: 'flex', alignItems: 'center' }}>{children}</Box>
  </Box>
);

const StilKnop = ({ children, onClick, disabled, href, sx }) => (
  <Box
    component={href ? 'a' : 'button'} type={href ? undefined : 'button'} href={href}
    target={href ? '_blank' : undefined} rel={href ? 'noopener noreferrer' : undefined}
    onClick={onClick} disabled={disabled}
    sx={{
      border: 0, bgcolor: 'transparent', p: 0, cursor: disabled ? 'default' : 'pointer', textDecoration: 'none',
      fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, opacity: disabled ? 0.5 : 1,
      '&:hover': { color: TEKST, textDecoration: 'underline' }, ...sx,
    }}
  >
    {children}
  </Box>
);

/** Subtakenlijst in het paneel: rondje, naam (klik = openen), stil "+ subtaak". */
function Subtaken({ lijst, kanToevoegen, alleenLezen, onOpen, onVink, onVoegToe, bewaarRef }) {
  const [invoer, setInvoer] = useState(false);
  const [naam, setNaam] = useState('');
  const [bezig, setBezig] = useState(false);
  // Ref naast de state: Enter en "paneel sluit" kort na elkaar mogen nooit twee POSTs geven.
  const bezigRef = useRef(false);
  const bewaar = async () => {
    const schoon = naam.trim();
    if (!schoon || bezigRef.current) return;
    bezigRef.current = true;
    setBezig(true);
    const gelukt = await onVoegToe(schoon);
    bezigRef.current = false;
    setBezig(false);
    if (gelukt) setNaam('');
  };
  // Het paneel sluit zonder Enter (bv. omdat de assistent opengaat): een getypte
  // subtaaknaam dan via hetzelfde pad als Enter bewaren, niet stil weggooien.
  useEffect(() => {
    if (!bewaarRef) return undefined;
    bewaarRef.current = bewaar;
    return () => { bewaarRef.current = null; };
  });
  if (!lijst.length && !kanToevoegen) return null;
  const af = lijst.filter((s) => s.isComplete).length;
  return (
    <Box component="section" aria-label="Subtaken" sx={{ mt: 2.5 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600, color: GEDEMPT, mb: 0.5 }}>
        Subtaken
        {lijst.length > 0 && <Box component="span" sx={{ fontWeight: 400, color: ZACHT }}>{` · ${af}/${lijst.length}`}</Box>}
      </Typography>
      {lijst.map((s) => (
        <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25, mx: -0.75, px: 0.25, borderRadius: 1, '&:hover': { bgcolor: HOVER } }}>
          <IconButton size="small" disabled={alleenLezen} onClick={() => onVink(s)}
            aria-label={s.isComplete ? `${s.name} heropenen` : `${s.name} afvinken`}
            sx={{ p: 0.25, color: s.isComplete ? ACCENT : ZACHT, '&:hover': { color: ACCENT } }}>
            {s.isComplete ? <KlaarIcon sx={{ fontSize: 17 }} /> : <OpenIcon sx={{ fontSize: 17 }} />}
          </IconButton>
          <Box component="button" type="button" onClick={() => onOpen(s)} aria-label={`Open subtaak ${s.name}`}
            sx={{
              flex: 1, minWidth: 0, border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', textAlign: 'left',
              fontFamily: FONT, fontSize: 13, color: s.isComplete ? ZACHT : TEKST,
              textDecoration: s.isComplete ? 'line-through' : 'none',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
            {s.name || 'Naamloos'}
          </Box>
          {(s.assigneeNames || []).length > 0 && (
            <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: ZACHT, flexShrink: 0 }}>{s.assigneeNames.join(', ')}</Typography>
          )}
        </Box>
      ))}
      {kanToevoegen && (invoer ? (
        <TextField
          autoFocus fullWidth variant="standard" value={naam} disabled={bezig} placeholder="Subtaak… (Enter)"
          inputProps={{ 'aria-label': 'Nieuwe subtaak' }} InputProps={{ disableUnderline: true }}
          onChange={(e) => setNaam(e.target.value)}
          onBlur={() => { if (!naam.trim()) setInvoer(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); bewaar(); }
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setNaam(''); setInvoer(false); }
          }}
          sx={{ '& .MuiInputBase-input': { fontFamily: FONT, fontSize: 13, py: 0.5 } }}
        />
      ) : (
        <StilKnop onClick={() => setInvoer(true)} sx={{ color: ZACHT, mt: 0.25 }}>+ subtaak</StilKnop>
      ))}
    </Box>
  );
}

// ── lezen ──

/**
 * Maakt van een bestaande schrijffunctie `(id, velden) => Promise<boolean>` (bv.
 * handleTaskUpdate op projectdetail) een `onWijzig` mét ongedaan maken.
 */
export const metOngedaan = (schrijf) => async (id, velden, { melding, terug } = {}) => {
  const gelukt = await schrijf(id, velden);
  if (gelukt !== false && terug && Object.keys(terug).length) {
    biedOngedaanAan(melding || 'Taak bijgewerkt', async () => {
      if ((await schrijf(id, terug)) === false) throw new Error('terugzetten mislukt');
    });
  }
  return gelukt !== false;
};

/** Het verse record, gestempeld met het gevraagde id (zo hoort het altijd bij één taak). */
async function haalVers(id) {
  const r = await authFetch(`${AI_ENGINE_URL}/api/v1/mirror/tasks?ids=${encodeURIComponent(id)}`);
  if (!r.ok) return null;
  const lijst = await r.json().catch(() => []);
  const rec = Array.isArray(lijst) ? lijst[0] : null;
  const t = rec ? naarTaak(rec) : null;
  return t ? { ...t, id } : null;
}

async function haalDeliverableNaam(id) {
  const r = await authFetch(`${AI_ENGINE_URL}/api/v1/mirror/deliverables?ids=${encodeURIComponent(id)}`);
  if (!r.ok) return '';
  const lijst = await r.json().catch(() => []);
  const f = (Array.isArray(lijst) ? lijst[0] : null)?.fields || {};
  return f['Naam van Deliverable'] || f.Naam || '';
}

/** `{ parent, subtasks }` of null als de engine het (nog) niet levert. */
async function haalDetail(id) {
  const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(id)}/detail`);
  if (!r.ok) return null;
  const d = await r.json().catch(() => null);
  if (!d || !Array.isArray(d.subtasks)) return null;
  return { parent: d.parent?.id ? d.parent : null, subtasks: d.subtasks };
}

const kanOphalen = (id) => Boolean(id) && !/^(outlook:|temp-|at-temp)/.test(String(id));

/**
 * @param taak          taak in elke vorm (zie naarTaak)
 * @param open / onClose
 * @param onWijzig      optioneel `(id, velden, { melding, terug }) => Promise<boolean>`; regelt zelf de undo
 * @param onBijgewerkt  `(id, velden)` na een eigen schrijfactie of undo (lokale lijst bijwerken)
 * @param onVerwijder   optioneel `(id) => Promise` (gooit bij een fout); anders DELETE /Api/tasks/:id
 * @param onVerwijderd  `(id)` na verwijderen
 * @param personen      personen in elke vorm; zonder: /Api/planner-todo/persons (gedeelde cache)
 * @param onNaarProject `(projectId)` — stille link "Naar project"
 * @param onNaarVandaag / onDupliceer / metPrive / onLokaal / regels — Dagstart-extra's
 * @param extraActies   `[{ label, onClick }]` — schermeigen acties (bv. Vandaag: tijdblok wissen)
 * @param alleenLezen   bv. een afspraak uit Outlook: tonen, niet bewerken
 * @param onOpenTaak    optioneel `({ id, name })` — een subtaak of de ouder openen; zonder
 *                      toont het paneel die taak zelf (het scherm houdt dan de oude selectie)
 * @param onAangemaakt  `(record)` na "+ subtaak" (lokale lijst bijwerken)
 * @param onWeggehaald  `(id)` als het toevoegen van een subtaak ongedaan is gemaakt
 * @param onOuderGewijzigd `(id, nieuweOuder|null, oudeOuder)` na "losmaken" (of het ongedaan maken)
 * @param focusVeld     optioneel `'feedback'`: bij openen meteen naar het feedbackveld (PL-todo)
 */
export default function TaakPaneel({
  taak: invoerProp, open, onClose, onWijzig: onWijzigProp, onBijgewerkt, onVerwijder, onVerwijderd,
  personen: personenProp, onNaarProject, onNaarVandaag, onDupliceer, metPrive = false, onLokaal, regels,
  extraActies = [], alleenLezen: alleenLezenProp = false,
  onOpenTaak, onAangemaakt, onWeggehaald, onOuderGewijzigd, focusVeld,
}) {
  const navigate = useNavigate();
  const paneelRef = useRef(null);
  // Een subtaak of ouder die het paneel zelf opende (scherm zonder onOpenTaak). Het scherm
  // houdt die taak niet bij, dus dan schrijft het paneel zelf (geen onWijzig).
  const [zelfGeopend, setZelfGeopend] = useState(null);
  const invoerPropId = invoerProp?.id;
  useEffect(() => { setZelfGeopend(null); }, [invoerPropId, open]);
  const invoer = zelfGeopend || invoerProp;
  const onWijzig = zelfGeopend ? undefined : onWijzigProp;
  const basis = useMemo(() => naarTaak(invoer), [invoer]);
  const id = basis?.id;

  // Vers record uit de spiegel: vult aan wat het scherm niet meegaf (het Rooster kent geen
  // opmerking; Dagstart geen subtaken) — anders overschrijft bewerken een bestaande waarde.
  const [vers, setVers] = useState(null);
  // Stand van het ophalen, gestempeld met het taak-id: bij een andere taak is het meteen
  // weer "laden" (ook in de render vóór het effect), nooit de stand van de vorige taak.
  const [versStand, setVersStand] = useState({ id: null, status: 'laden' });
  const [versPoging, setVersPoging] = useState(0);
  const [deliverableNaam, setDeliverableNaam] = useState('');
  // Eigen wijzigingen per taak-id, tot het scherm ze terugspiegelt. Per id, zodat een
  // PATCH-antwoord of undo van taak A die binnenkomt terwijl B open staat, alleen A raakt.
  // Met `onWijzig` houdt het scherm de taak zelf bij en volgen we alleen wat het doorgeeft.
  const [eigen, setEigen] = useState({});
  const [historieVersie, setHistorieVersie] = useState(0);
  const [bezig, setBezig] = useState(false);

  useEffect(() => { if (!open) setEigen({}); }, [open]);
  useEffect(() => {
    setVers(null);
    setDeliverableNaam('');
    if (!open || !kanOphalen(id)) return undefined;
    setVersStand({ id, status: 'laden' });
    let weg = false;
    haalVers(id)
      .then((t) => {
        if (weg) return;
        setVers(t);
        setVersStand({ id, status: t ? 'klaar' : 'mislukt' });
      })
      .catch(() => { if (!weg) setVersStand({ id, status: 'mislukt' }); });
    return () => { weg = true; };
  }, [id, open, versPoging]);
  let versStatus = 'nvt';
  if (kanOphalen(id)) versStatus = versStand.id === id ? versStand.status : 'laden';

  const taak = useMemo(() => {
    const samen = vulAan(basis, vers && vers.id === id ? vers : null);
    if (!samen) return null;
    return pasToe(samen, eigen[id] || {});
  }, [basis, vers, eigen, id]);

  useEffect(() => {
    if (!open || !taak?.deliverableId || taak.deliverableNaam) return undefined;
    let weg = false;
    haalDeliverableNaam(taak.deliverableId).then((n) => { if (!weg) setDeliverableNaam(n); }).catch(() => {});
    return () => { weg = true; };
  }, [open, taak?.deliverableId, taak?.deliverableNaam]);

  // Personen voor de uitvoerder-keuze.
  const [opgehaald, setOpgehaald] = useState(null);
  useEffect(() => {
    if (!open || (personenProp && personenProp.length)) return undefined;
    let weg = false;
    haalPersonen().then((p) => { if (!weg) setOpgehaald(p || []); }).catch(() => { if (!weg) setOpgehaald([]); });
    return () => { weg = true; };
  }, [open, personenProp]);
  const opties = useMemo(() => persoonOpties(personenProp && personenProp.length ? personenProp : opgehaald), [personenProp, opgehaald]);

  // Privé (alleen Dagstart): verberg deze taak op het dagstart-scherm van collega's.
  const { instance } = useMsal();
  const mijnEmail = instance?.getActiveAccount?.()?.username || '';
  const [priveAan, setPriveAan] = useState(false);
  useEffect(() => { if (metPrive && id) setPriveAan(isEigenPrive(id, mijnEmail)); }, [metPrive, id, mijnEmail]);

  // Esc sluit — behalve als je in een open keuzelijst of bevestigdialoog zit.
  useEffect(() => {
    if (!open) return undefined;
    const bijToets = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const doel = e.target instanceof Element ? e.target : null;
      if (doel && !paneelRef.current?.contains(doel) && doel.closest('[role="presentation"],[role="dialog"],[role="listbox"],[role="menu"]')) return;
      onClose?.();
    };
    document.addEventListener('keydown', bijToets);
    return () => document.removeEventListener('keydown', bijToets);
  }, [open, onClose]);

  // Taakpaneel en assistent staan allebei rechts en overlapten elkaar. Wie als laatste
  // opengaat wint: een taak openen sluit de assistent, de assistent openen sluit het paneel.
  // Een veld dat nog in bewerking is, wordt eerst bewaard (blur = opslaan, net als bij ×).
  const assistent = useAssistantZichtbaar();
  const assistentOpen = Boolean(assistent?.isOpen);
  const sluitAssistent = assistent?.sluit;
  const vorigeRef = useRef({ paneel: false, id: null, assistent: false });
  const subtaakBewaarRef = useRef(null);
  useEffect(() => {
    const v = vorigeRef.current;
    const paneelNet = Boolean(open) && (!v.paneel || v.id !== id);
    const assistentNet = assistentOpen && !v.assistent;
    vorigeRef.current = { paneel: Boolean(open), id, assistent: assistentOpen };
    if (!open || !assistentOpen) return;
    if (assistentNet) {
      subtaakBewaarRef.current?.();
      const actief = document.activeElement;
      if (actief && paneelRef.current?.contains(actief)) actief.blur?.();
      onClose?.();
    } else if (paneelNet) {
      sluitAssistent?.();
    }
  }, [open, id, assistentOpen, sluitAssistent, onClose]);

  // Namen van uitvoerders die we al eens zagen (lookup of gekozen optie). Een scherm dat na
  // een wijziging een record zonder lookup terugspiegelt, of een uitvoerder die niet in de
  // personenlijst van dat scherm staat (freelancer), toonde anders "Onbekend".
  const namenGeheugen = useRef(new Map());

  // Subtaken + ouder (GET /Api/tasks/:id/detail). Een oudere engine kent die route of de
  // velden niet: dan `null` en tonen we alleen het oude "3 subtaken"-regeltje.
  const [detail, setDetail] = useState(null);
  const [detailVersie, setDetailVersie] = useState(0);
  useEffect(() => {
    setDetail(null);
    if (!open || !kanOphalen(id)) return undefined;
    let weg = false;
    haalDetail(id).then((d) => { if (!weg) setDetail(d); }).catch(() => {});
    return () => { weg = true; };
  }, [id, open, detailVersie]);

  const openTaak = useCallback((t) => {
    if (!t?.id) return;
    if (onOpenTaak) onOpenTaak(t);
    else setZelfGeopend({ id: t.id, name: t.name });
  }, [onOpenTaak]);

  /** Past velden toe op het record van `taakId` — nooit op een taak die intussen open staat. */
  const pasLokaalToe = useCallback((taakId, velden, { eigenBijhouden = true } = {}) => {
    if (eigenBijhouden) setEigen((e) => ({ ...e, [taakId]: { ...(e[taakId] || {}), ...velden } }));
    setVers((v) => (v && v.id === taakId ? pasToe(v, velden) : v));
    setHistorieVersie((n) => n + 1);
  }, []);

  const patch = useCallback(async (taakId, velden) => {
    const r = await schrijfTaak(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taakId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden),
    });
    await leesJson(r, 'Taak bijwerken');
  }, []);

  /** Eén schrijfpad voor elk veld: opslaan, lokaal tonen, undo aanbieden. */
  const wijzig = useCallback(async (velden, melding) => {
    if (!taak?.id) return false;
    // Een veld waarvan we de huidige waarde nog niet kennen: niet schrijven (het veld is dan
    // ook niet bewerkbaar), anders wordt '' de ongedaan-waarde van een bestaande tekst.
    if (!kentVelden(taak, velden)) return false;
    const taakId = taak.id;
    const terug = terugVoor(taak, velden);
    setBezig(true);
    try {
      if (onWijzig) {
        const gelukt = await onWijzig(taakId, velden, { melding, terug });
        if (gelukt !== false) pasLokaalToe(taakId, velden, { eigenBijhouden: false });
        return gelukt !== false;
      }
      await patch(taakId, velden);
      pasLokaalToe(taakId, velden);
      onBijgewerkt?.(taakId, velden);
      biedOngedaanAan(melding || 'Taak bijgewerkt', async () => {
        await patch(taakId, terug);
        pasLokaalToe(taakId, terug);
        onBijgewerkt?.(taakId, terug);
      });
      return true;
    } catch (e) {
      meldFout(e, 'Taak bijwerken');
      return false;
    } finally {
      setBezig(false);
    }
  }, [taak, onWijzig, onBijgewerkt, patch, pasLokaalToe]);

  /** Subtaak afvinken (of weer open) vanuit de lijst in het paneel, met ongedaan maken. */
  const vinkSubtaak = async (sub) => {
    const velden = afvinkVelden({ afgerond: sub.isComplete, status: sub.status });
    const terug = { Afgerond: Boolean(sub.isComplete), PrioNr: sub.status || 'Geen prio' };
    const melding = sub.isComplete ? `${sub.name} weer open` : `${sub.name} afgerond`;
    const zetLokaal = (af) => setDetail((d) => (d ? {
      ...d, subtasks: d.subtasks.map((x) => (x.id === sub.id ? { ...x, isComplete: af, status: af ? 'Afgerond' : (velden.PrioNr || x.status) } : x)),
    } : d));
    zetLokaal(!sub.isComplete);
    try {
      if (onWijzig) {
        if ((await onWijzig(sub.id, velden, { melding, terug })) === false) throw new Error('niet opgeslagen');
      } else {
        await patch(sub.id, velden);
        onBijgewerkt?.(sub.id, velden);
        biedOngedaanAan(melding, async () => {
          await patch(sub.id, terug);
          onBijgewerkt?.(sub.id, terug);
          setDetailVersie((n) => n + 1);
        });
      }
    } catch (e) {
      zetLokaal(Boolean(sub.isComplete));
      if (e?.message !== 'niet opgeslagen') meldFout(e, 'Subtaak bijwerken');
    }
  };

  /** "+ subtaak": erft project, deliverable en type van deze taak (dat doet de engine). */
  const voegSubtaakToe = async (naam) => {
    const ouderId = taak?.id;
    if (!ouderId) return false;
    try {
      const r = await schrijfTaak(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ NaamTaak: naam, parentTaskId: ouderId }),
      });
      const d = await leesJson(r, 'Subtaak toevoegen');
      const nieuwId = d?.task?.id || d?.id;
      if (!nieuwId) throw new Error('Subtaak toevoegen: geen id terug');
      setDetail((dd) => (dd ? { ...dd, subtasks: [...dd.subtasks, { id: nieuwId, name: naam, isComplete: false, status: 'Geen prio' }] } : dd));
      onAangemaakt?.({
        id: nieuwId, parentTaskId: ouderId, subtaskIds: [],
        fields: {
          PrioNr: 'Geen prio', ...(d?.task?.fields || {}), NaamTaak: naam, 'Parent task': [ouderId],
          ...(taak.projectId ? { Project: [taak.projectId] } : {}),
          ...(taak.projectNaam ? { 'Prnr en Naam': [taak.projectNaam] } : {}),
          ...(taak.deliverableId ? { Deliverable: [taak.deliverableId] } : {}),
          ...(taak.type ? { Type: taak.type } : {}),
        },
      });
      biedOngedaanAan(`Subtaak "${naam}" toegevoegd`, async () => {
        const rr = await schrijfTaak(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(nieuwId)}`, { method: 'DELETE' });
        await leesJson(rr, 'Subtaak weghalen');
        setDetail((dd) => (dd ? { ...dd, subtasks: dd.subtasks.filter((x) => x.id !== nieuwId) } : dd));
        onWeggehaald?.(nieuwId);
      });
      return true;
    } catch (e) {
      meldFout(e, 'Subtaak toevoegen');
      return false;
    }
  };

  /** Losmaken: de subtaak wordt een gewone taak. Ongedaan maken hangt hem terug. */
  const zetOuder = async (nieuw, oud) => {
    const taakId = taak?.id;
    const r = await schrijfTaak(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taakId)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parentTaskId: nieuw }),
    });
    await leesJson(r, 'Subtaak losmaken');
    onOuderGewijzigd?.(taakId, nieuw, oud);
    setDetailVersie((n) => n + 1);
    setHistorieVersie((n) => n + 1);
  };
  const maakLos = async () => {
    const oud = detail?.parent;
    if (!oud) return;
    try {
      await zetOuder(null, oud.id);
      biedOngedaanAan(`"${taak.naam}" losgemaakt van "${oud.name}"`, () => zetOuder(oud.id, null));
    } catch (e) {
      meldFout(e, 'Subtaak losmaken');
    }
  };

  const verwijder = async () => {
    if (!taak?.id) return;
    const akkoord = await bevestig({
      titel: 'Taak verwijderen?',
      tekst: `"${taak.naam || 'Deze taak'}" wordt definitief verwijderd.\n\n`
        + 'Dit kun je niet ongedaan maken. Is de taak gewoon klaar, vink hem dan af.',
      knop: 'Verwijderen',
      gevaarlijk: true,
    });
    if (!akkoord) return;
    setBezig(true);
    try {
      if (onVerwijder) {
        await onVerwijder(taak.id);
      } else {
        const r = await schrijfTaak(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taak.id)}`, { method: 'DELETE' });
        await leesJson(r, 'Taak verwijderen');
        toast.success('Taak verwijderd');
      }
      onVerwijderd?.(taak.id);
      onClose?.();
    } catch (e) {
      meldFout(e, 'Taak verwijderen');
    } finally {
      setBezig(false);
    }
  };

  const wisselPrive = async () => {
    try {
      await zetPrive(taak.id, !priveAan, mijnEmail);
      setPriveAan(!priveAan);
    } catch { /* volgende poging kan gewoon */ }
  };

  if (!taak) return null;

  const uitOutlook = Boolean(invoer?.uitOutlook) || String(taak.id || '').startsWith('outlook:');
  const alleenLezen = alleenLezenProp || uitOutlook;
  // Velden die het scherm niet meegaf en het verse record (nog) niet aanvulde: niet
  // bewerkbaar, stil "laden…" — of na een mislukte fetch "niet geladen".
  const onbekend = (...sleutels) => sleutels.some((k) => taak[k] === undefined);
  const laadTekst = versStatus === 'laden' ? 'laden…' : 'niet geladen';
  const nietGeladen = versStatus === 'mislukt' && !alleenLezen && onbekend(
    'naam', 'status', 'afgerond', 'type', 'start', 'eind', 'deadline', 'deadlineSoort', 'uren', 'opmerking', 'assigneeIds',
  );
  const vandaagISO = formatDateISO(new Date());
  const teLaat = isTeLaat(taak, vandaagISO);
  const kop = contextRegel({ ...taak, deliverableNaam: taak.deliverableNaam || deliverableNaam });
  const dagen = werkdagenInBereik(taak.start, taak.eind);
  const schatting = regels ? effectiveTaskHours({ hours: taak.uren, type: taak.type }, regels) : null;
  const namenPerId = Object.fromEntries(opties.map((o) => [o.id, o.label]));
  const idsNu = taak.assigneeIds || [];
  const namenNu = taak.assigneeNamen || [];
  // Alleen onthouden als ids en namen één-op-één naast elkaar liggen.
  if (idsNu.length && idsNu.length === namenNu.length) {
    idsNu.forEach((pid, i) => { if (namenNu[i]) namenGeheugen.current.set(pid, namenNu[i]); });
  }
  const gekozen = idsNu.map((pid, i) => ({
    id: pid,
    label: namenPerId[pid] || namenNu[i] || namenGeheugen.current.get(pid) || 'Onbekend',
  }));
  // Een uitvoerder buiten de personenlijst (bv. freelancer) blijft kiesbaar/zichtbaar.
  const optiesMetGekozen = [...opties, ...gekozen.filter((p) => !namenPerId[p.id])];
  const isDraaidag = taak.type === 'Draaidag' || taak.status === 'Draaidag';
  const links = linksUit(taak.opmerking);
  const airtable = airtableUrl(taak.id);
  const startTijd = invoer?.startTime;
  const eindTijd = invoer?.endTime;

  const naarCallsheet = () => {
    const q = new URLSearchParams({ new: '1', naam: taak.naam || '' });
    if (taak.projectId) q.set('project', taak.projectId);
    onClose?.();
    navigate(`/callsheets?${q.toString()}`);
  };

  const onderaan = [
    invoer?.lastModifiedBy ? `Laatst gewijzigd door ${invoer.lastModifiedBy}${invoer.lastModified ? ` · ${datumLang(String(invoer.lastModified).slice(0, 10))}` : ''}` : '',
    taak.aangemaakt ? `Aangemaakt op ${datumLang(taak.aangemaakt)}` : '',
  ];

  return (
    <Drawer
      anchor="right" variant="persistent" open={open}
      // Laat de roosterrij weten dat Esc nu het paneel sluit (en de taken niet inklapt).
      data-taakpaneel={open ? 'open' : 'dicht'}
      PaperProps={{
        ref: paneelRef, role: 'complementary', 'aria-label': 'Taak',
        sx: { ...onderTestbalk, width: { xs: '100%', md: 760 }, maxWidth: '100%', borderLeft: `1px solid ${LIJN}`, boxShadow: '-12px 0 32px rgba(15,23,42,0.08)' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: FONT }}>
        {/* Kop: rondje + titel, daaronder project · deliverable/fase */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
          <Tooltip title={alleenLezen ? '' : taak.afgerond ? 'Weer openzetten' : 'Afvinken'}>
            <span>
              <IconButton
                size="small" disabled={alleenLezen || bezig || onbekend('afgerond', 'status')}
                aria-label={taak.afgerond ? 'Taak heropenen' : 'Taak afvinken'}
                onClick={() => wijzig(afvinkVelden(taak), taak.afgerond ? `${taak.naam} weer open` : `${taak.naam} afgerond`)}
                sx={{ mt: 0.25, color: taak.afgerond ? ACCENT : ZACHT, '&:hover': { color: ACCENT } }}
              >
                {taak.afgerond ? <KlaarIcon sx={{ fontSize: 24 }} /> : <OpenIcon sx={{ fontSize: 24 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StilTekst
              waarde={taak.naam} label="Taaknaam" disabled={alleenLezen || onbekend('naam')}
              plaatshouder={onbekend('naam') ? laadTekst : 'Taaknaam'}
              onBewaar={(v) => { if (v.trim()) wijzig({ NaamTaak: v.trim() }, 'Taaknaam gewijzigd'); }}
              sx={{
                fontFamily: KOP_FONT, fontSize: 20, fontWeight: 700, lineHeight: 1.3,
                ...(taak.afgerond ? { color: ZACHT, textDecoration: 'line-through' } : {}),
              }}
            />
            {detail?.parent && (
              <Typography component="div" sx={{ fontFamily: FONT, fontSize: 12.5, color: ZACHT, mt: 0.25, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box component="button" type="button" onClick={() => openTaak(detail.parent)}
                  aria-label={`Open bovenliggende taak ${detail.parent.name}`}
                  sx={{ border: 0, bgcolor: 'transparent', p: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left', '&:hover': { color: TEKST, textDecoration: 'underline' } }}>
                  onder: {detail.parent.name || 'taak'}
                </Box>
                {!alleenLezen && <StilKnop onClick={maakLos} sx={{ fontSize: 12.5, color: ZACHT }}>losmaken</StilKnop>}
              </Typography>
            )}
            {kop && (
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: GEDEMPT, mt: 0.25 }}>
                {onNaarProject && taak.projectId ? (
                  <Box component="button" type="button" onClick={() => onNaarProject(taak.projectId)}
                    sx={{ border: 0, bgcolor: 'transparent', p: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left', '&:hover': { color: TEKST, textDecoration: 'underline' } }}>
                    {kop}
                  </Box>
                ) : kop}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Sluiten" sx={{ color: ZACHT }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{
          flex: 1, minHeight: 0, overflowY: 'auto', px: 3, pb: 2,
          display: 'grid', gap: 4, alignContent: 'start',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 220px' },
        }}>
          {/* Rechts (op smal scherm bovenaan): eigenschappen */}
          <Box sx={{ order: { xs: 0, md: 1 } }}>
            <Eigenschap label="Status">
              {onbekend('status', 'afgerond') ? <Onbekend tekst={laadTekst} /> : (
                <StilKeuze
                  waarde={taak.status} opties={STATUS_OPTIES} label="Status" leeg="Geen prio" disabled={alleenLezen || bezig}
                  onKies={(s) => wijzig(statusVelden(taak, s), `Status → ${s}`)}
                />
              )}
            </Eigenschap>
            <Eigenschap label="Uitvoerder">
              {onbekend('assigneeIds') && <Onbekend tekst={alleenLezen ? '—' : laadTekst} />}
              {!onbekend('assigneeIds') && alleenLezen && (gekozen.map((p) => p.label).join(', ') || '—')}
              {!onbekend('assigneeIds') && !alleenLezen && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
                  <Autocomplete
                    multiple fullWidth size="small" disableClearable
                    options={optiesMetGekozen} value={gekozen}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    getOptionLabel={(o) => o.label || ''}
                    onChange={(_e, nieuw) => {
                      nieuw.forEach((p) => { if (p.label && p.label !== 'Onbekend') namenGeheugen.current.set(p.id, p.label); });
                      return wijzig({ Assignee: nieuw.map((p) => p.id) },
                      nieuw.length ? `Toegewezen aan ${nieuw.map((p) => p.label).join(', ')}` : 'Uitvoerder gewist');
                    }}
                    renderTags={(waarden) => (
                      <Typography component="span" sx={{ fontFamily: FONT, fontSize: 13, color: TEKST, pl: 0.25 }}>
                        {waarden.map((w) => w.label).join(', ')}
                      </Typography>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} variant="standard" placeholder={gekozen.length ? '' : 'Kies…'}
                        inputProps={{ ...params.inputProps, 'aria-label': 'Uitvoerder' }}
                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                        sx={{ '& .MuiInputBase-root': { fontFamily: FONT, fontSize: 13, borderRadius: 1, px: 1, mx: -1, '&:hover': { bgcolor: HOVER } } }} />
                    )}
                    ListboxProps={{ sx: { fontFamily: FONT, fontSize: 13 } }}
                  />
                  {taak.assigneeIds?.[0] && (
                    <UitvoerderAgenda personId={taak.assigneeIds[0]} taakStart={taak.start} taakEind={taak.eind} />
                  )}
                </Box>
              )}
            </Eigenschap>
            <Eigenschap label={startTijd ? 'Wanneer' : 'Start – eind'}>
              {startTijd && <span>{datumLang(taak.start)} {startTijd}{eindTijd ? `–${eindTijd}` : ''}</span>}
              {!startTijd && onbekend('start', 'eind') && <Onbekend tekst={laadTekst} />}
              {!startTijd && !onbekend('start', 'eind') && (
                <Box sx={{ width: '100%' }}>
                  <StilDatum waarde={taak.start} label="Startdatum" disabled={alleenLezen || bezig}
                    onBewaar={(d) => wijzig(
                      // Een start ná de eind schuift de eind mee — anders een taak die eindigt vóór hij begint.
                      d && taak.eind && d > taak.eind ? { StartDateUitvoerder: d, EndDateUitvoerder: d } : { StartDateUitvoerder: d },
                      d ? `Start → ${datumLang(d)}` : 'Startdatum gewist')} />
                  <StilDatum waarde={taak.eind} label="Einddatum" disabled={alleenLezen || bezig}
                    onBewaar={(d) => wijzig({ EndDateUitvoerder: d }, d ? `Eind → ${datumLang(d)}` : 'Einddatum gewist')} />
                  {dagen.length > 1 && (
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: ZACHT }}>{dagen.length} werkdagen</Typography>
                  )}
                </Box>
              )}
            </Eigenschap>
            <Eigenschap label="Deadline">
              <Box sx={{ width: '100%' }}>
                {onbekend('deadline') ? <Onbekend tekst={laadTekst} /> : (
                  <StilDatum waarde={taak.deadline} label="Deadline" rood={teLaat} disabled={alleenLezen || bezig}
                    onBewaar={(d) => wijzig({ Deadline: d }, d ? `Deadline → ${datumLang(d)}` : 'Deadline gewist')} />
                )}
                {/* Soort mee kunnen zetten is geen luxe: alleen Hard en Klant zetten een taak in
                    ACHTERSTALLIG zodra de datum verstrijkt. */}
                {!onbekend('deadlineSoort') && (
                  <StilKeuze waarde={taak.deadlineSoort} opties={DEADLINE_SOORTEN} label="Soort deadline" leeg="Soort…"
                    disabled={alleenLezen || bezig}
                    onKies={(s) => wijzig({ TypeDeadline: s }, `Soort deadline → ${s}`)} />
                )}
                {teLaat && (
                  <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: ROOD }}>te laat</Typography>
                )}
              </Box>
            </Eigenschap>
            <Eigenschap label="Uren">
              {uitOutlook ? (
                <span>{invoer?.hours ? urenTekst(invoer.hours) : '—'} <span style={{ color: ZACHT }}>(via de agenda)</span></span>
              ) : onbekend('uren') ? <Onbekend tekst={laadTekst} /> : (
                <Box sx={{ width: '100%' }}>
                  <StilTekst
                    waarde={taak.uren ? String(taak.uren).replace('.', ',') : ''} label="Uren" plaatshouder="—" disabled={bezig}
                    onBewaar={(v) => {
                      const schoon = String(v).replace(',', '.').trim();
                      if (schoon === '') { wijzig({ UrenWerk: null }, 'Uren gewist'); return; }
                      const n = Number(schoon);
                      if (!Number.isFinite(n) || n < 0) { toast.error('Vul een aantal uren in, bijvoorbeeld 4 of 1,5'); return; }
                      wijzig({ UrenWerk: n }, `Uren → ${String(n).replace('.', ',')}`);
                    }}
                    sx={{ fontSize: 13 }}
                  />
                  {(schatting?.estimated || (taak.uren && dagen.length > 1)) && (
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: ZACHT }}>
                      {schatting?.estimated ? `geschat op ${urenTekst(schatting.val)}` : `${urenTekst(taak.uren / dagen.length)} per dag`}
                    </Typography>
                  )}
                </Box>
              )}
            </Eigenschap>
            <Eigenschap label="Type">
              {onbekend('type') ? <Onbekend tekst={laadTekst} /> : (
                <StilKeuze waarde={taak.type} opties={TYPE_OPTIES} label="Type" disabled={alleenLezen || bezig}
                  onKies={(t) => wijzig({ Type: t }, `Type → ${t}`)} />
              )}
            </Eigenschap>
            {(taak.deliverableNaam || deliverableNaam || taak.deliverableId) && (
              <Eigenschap label="Deliverable">{taak.deliverableNaam || deliverableNaam || '—'}</Eigenschap>
            )}
            {taak.fase && <Eigenschap label="Fase">{taak.fase}</Eigenschap>}
            {invoer?.projectleider && <Eigenschap label="Projectleider">{invoer.projectleider}</Eigenschap>}
          </Box>

          {/* Links: opmerking, subtaken, feedback, historie */}
          <Box sx={{ order: { xs: 1, md: 0 }, minWidth: 0 }}>
            {uitOutlook && (
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: GEDEMPT, mb: 2 }}>
                Deze afspraak komt rechtstreeks uit Outlook. Aanpassen doe je in je agenda.
              </Typography>
            )}
            {nietGeladen && (
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: ZACHT, mb: 2 }}>
                details niet geladen —{' '}
                <StilKnop onClick={() => setVersPoging((n) => n + 1)} sx={{ fontSize: 12.5, color: ZACHT }}>opnieuw</StilKnop>
              </Typography>
            )}
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600, color: GEDEMPT, mb: 0.5 }}>Opmerking</Typography>
            <StilTekst
              meerRegels waarde={taak.opmerking} label="Opmerking" disabled={alleenLezen || onbekend('opmerking')}
              plaatshouder={onbekend('opmerking') ? laadTekst : 'Voeg een opmerking toe…'}
              onBewaar={(v) => wijzig({ Opmerking: v }, 'Opmerking bijgewerkt')}
              sx={{ fontSize: 13.5, lineHeight: 1.6, minHeight: 64 }}
            />
            {links.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.75 }}>
                {links.map((u) => {
                  let host = 'link';
                  try { host = new URL(u).hostname.replace(/^www\./, ''); } catch { /* laat 'link' staan */ }
                  const label = /outlook\.(office365|office|live)\.com/.test(host) ? 'Open mail in Outlook' : host;
                  return <StilKnop key={u} href={u}>{label} ↗</StilKnop>;
                })}
              </Box>
            )}
            {detail ? (
              <Subtaken
                lijst={detail.subtasks} kanToevoegen={!detail.parent && !alleenLezen} alleenLezen={alleenLezen}
                onOpen={openTaak} onVink={vinkSubtaak} onVoegToe={voegSubtaakToe} bewaarRef={subtaakBewaarRef}
              />
            ) : taak.subtaken > 0 && (
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, mt: 2 }}>
                {taak.subtaken} {taak.subtaken === 1 ? 'subtaak' : 'subtaken'}{taak.openSubtaken ? ', nog open' : ''}
              </Typography>
            )}
            {(invoer?.feedbackVereist || invoer?.feedbackLink || invoer?.feedbackTekst) && !uitOutlook && (
              <Box sx={{ mt: 2 }}><FeedbackVeld key={invoer?.id} taak={invoer} onLokaal={onLokaal} autoFocus={focusVeld === 'feedback' && !zelfGeopend} /></Box>
            )}
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${LIJN}` }}>
              <TaakHistorie taakId={taak.id} versie={historieVersie} onderaan={onderaan} />
            </Box>
          </Box>
        </Box>

        {/* Onderaan stil: de minder vaak gebruikte acties */}
        {!uitOutlook && (
          <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${LIJN}`, display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'center' }}>
            {onNaarVandaag && <StilKnop disabled={bezig} onClick={() => { onNaarVandaag(invoer); onClose?.(); }}>Naar vandaag</StilKnop>}
            {onDupliceer && (
              <StilKnop disabled={bezig} onClick={async () => { setBezig(true); try { await onDupliceer(invoer); } finally { setBezig(false); } }}>
                Dupliceren
              </StilKnop>
            )}
            {isDraaidag && <StilKnop onClick={naarCallsheet}>Callsheet maken</StilKnop>}
            {metPrive && (
              <StilKnop onClick={wisselPrive}>{priveAan ? 'Privé: alleen jij ziet deze taak' : 'Privé maken'}</StilKnop>
            )}
            {extraActies.map((a) => <StilKnop key={a.label} disabled={bezig} onClick={a.onClick}>{a.label}</StilKnop>)}
            {airtable && <StilKnop href={airtable}>Open in Airtable ↗</StilKnop>}
            <Box sx={{ flex: 1 }} />
            <StilKnop disabled={bezig} onClick={verwijder}>Verwijderen</StilKnop>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
